import { NBSP } from '@r1-frontend/ui-react/tokens/symbols';

import { ITariff } from '~/src/entities/formattedTariff';
import {
    filterAndTransformTariffs,
    ITariffsResult,
} from '~/src/features/internet/generateTariffSummaryTableFAQ/utils/getTariffsForTable';
import {
    transformConstructorForSummaryTable,
} from '~/src/features/internet/generateTariffSummaryTableFAQ/utils/transformConstructorForSummaryTable';
import {
    transformTariffForSummaryTable,
} from '~/src/features/internet/generateTariffSummaryTableFAQ/utils/transformTariffForSummaryTable';

interface IStructureHead {
    id: string,
    th: string,
    tdDescription?: string,
    tdValueFn: (tariff: ITariffsResult) => React.ReactNode,
}

const OFFER_CONTENTS: IStructureHead = {
    id: 'offerContents',
    th: 'Подписки',
    tdDescription: 'Подписки',
    tdValueFn: (tariff: ITariffsResult) => tariff.offerContents || '',
};

const ADDITIONAL_PRODUCTS: IStructureHead = {
    id: 'additionalProducts',
    th: 'Оборудование',
    tdDescription: 'Оборудование',
    tdValueFn: (tariff: ITariffsResult) => tariff.additionalProducts || '',
};

const TARIFF: IStructureHead = {
    id: 'title',
    th: 'Тариф',
    tdValueFn: (tariff: ITariffsResult) => `<b>${tariff.title}</b>`,
};

const SPEED: IStructureHead = {
    id: 'speed',
    th: 'Скорость\nинтернета',
    tdDescription: `Скорость${NBSP}интернета`,
    tdValueFn: (tariff: ITariffsResult) => tariff.speed,
};

const CHANNELS: IStructureHead = {
    id: 'channels',
    th: 'Количество\nканалов',
    tdDescription: `Количество${NBSP}каналов`,
    tdValueFn: (tariff: ITariffsResult) => tariff.channels,
};

const PRICE: IStructureHead = {
    id: 'price',
    th: 'Абонентская\nплата',
    tdDescription: `Абонентская${NBSP}плата`,
    tdValueFn: (tariff: ITariffsResult) => tariff.price,
};

// кнопки подключения
const BUTTON_FUNC: (code?: string) => IStructureHead = (code?: string) => ({
    id: 'button',
    th: '',
    tdValueFn: (tariff: ITariffsResult) => {
        const urlCodeParam = code ? `&code=${code}` : '';
        const text = tariff.buttonText || 'Подключить';
        return `<a href=${tariff.url}${urlCodeParam}>${text}</a>`;
    },
});

interface ITariffsTable {
    caption: string,
    head: IStructureHead[],
    rows: string[][],
}

export const getTariffsTable = ({ tariffsForPage, code }: {
    code?: string,
    tariffsForPage: ITariff[],
}): ITariffsTable => {
    const summaryInfo = {
        hasOfferContents: false,
        hasAdditionalProducts: false,
    };
    const getTransformTariffForSummaryTable = transformTariffForSummaryTable(summaryInfo);

    const { tariffs: tariffsResult, constructorTariffs: constructors } = filterAndTransformTariffs({
        tariffs: tariffsForPage,
        baseTransformFunction: getTransformTariffForSummaryTable,
    });

    const constructorRow = transformConstructorForSummaryTable({ ...summaryInfo, constructors });

    const optionalTd = [
        summaryInfo.hasAdditionalProducts && ADDITIONAL_PRODUCTS,
        summaryInfo.hasOfferContents && OFFER_CONTENTS,
    ].filter(Boolean) as IStructureHead[];

    const head = [
        TARIFF,
        SPEED,
        CHANNELS,
        ...optionalTd,
        PRICE,
        BUTTON_FUNC(code),
    ];

    const allTariffs = constructorRow ? [constructorRow, ...tariffsResult] : tariffsResult;
    const rows = allTariffs.map((tariff) => head.map(({ tdValueFn, tdDescription }) =>
        tdDescription
            ? `<span data-view='mobile'>${tdDescription}</span>${tdValueFn(tariff)}`
            : `${tdValueFn(tariff)}`));

    return {
        caption: 'Тарифы интернета',
        head,
        rows,
    };
};
